import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { LuDownload } from 'react-icons/lu';

import Table from '../components/common/Table.jsx';
import { BadgeSkeleton } from '../components/optimization/Skeletons.jsx';
import { CONST_SITE_LABEL } from '../components/common/Helper';

import UserInvoiceApiService from '../services/UserInvoiceApiService';
import DebugService from '../services/DebugService';

// Safe date format — old page crashed if the date was missing/invalid.
const fmtDate = (v) => {
  if (v == null || v === '') return '';
  try { return format(new Date(v), 'dd-MM-yyyy'); } catch { return ''; }
};

const invoiceColumns = [
  { key: 'id',           label: 'S.no', className: 'num' },
  { key: 'name',         label: 'Name' },
  { key: 'email',        label: 'Email' },
  { key: 'subscription', label: 'Subscription' },
  { key: 'amount',       label: 'Amount ($)', className: 'num' },
  { key: 'paidOn',       label: 'Paid On' },
  { key: 'dueDate',      label: 'Due Date' },
  {
    key: 'invoiceUrl', label: 'Invoice',
    render: (row) => row.invoiceUrl
      ? (
        <a href={row.invoiceUrl} target="_blank" rel="noreferrer" title="Download invoice" style={{ display: 'inline-flex' }}>
          <LuDownload color="#FF7A00" size={18} />
        </a>
      )
      : <span style={{ color: 'var(--muted)' }}>—</span>,
  },
];

export const UserInvoice = () => {
  const [rows, setRows]           = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = () => {
    setIsLoading(true);
    UserInvoiceApiService.getData()
      .then((res) => {
        DebugService.log('UserInvoice: API response', res);
        if (res?.error) DebugService.error('UserInvoice: error in response', res.error);
        const apiData = res?.data ?? [];
        setRows(apiData.map((d, i) => {
          const payload = d?.payload ?? {};
          const product = payload?.product ?? {};
          return {
            id: i + 1,
            name: payload?.name ?? '',
            email: payload?.email ?? '',
            subscription: product?.name ?? '',
            amount: product?.price ?? '',
            paidOn: fmtDate(d?.createdOn),
            dueDate: fmtDate(product?.recurring_date),
            invoiceUrl: payload?.custom?.invoiceURL ?? '',
          };
        }));
      })
      .catch((err) => DebugService.error('UserInvoice: error fetching data', err))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    document.title = `${CONST_SITE_LABEL} | User Invoice`;
    fetchData();
  }, []);

  return (
    <>
      <div className="breadcrumb">
        Settings <span style={{ color: 'var(--muted-2)' }}>›</span> <strong>Invoices</strong>
      </div>

      <div className="page-head">
        <div>
          <h1>
            Invoices{' '}
            {isLoading ? <BadgeSkeleton /> : <span className="badge">{rows.length} invoices</span>}
          </h1>
          <div className="meta">Your subscription billing history and downloadable invoices</div>
        </div>
        <div className="head-actions">
          <button className="btn ghost" onClick={fetchData}>↻ Refresh</button>
        </div>
      </div>

      <Table
        rows={rows}
        columns={invoiceColumns}
        loading={isLoading}
        skeletonRows={6}
        pageSizes={[10, 25, 50, 100]}
        defaultPageSize={10}
        emptyText="No invoices found."
      />
    </>
  );
};

export default UserInvoice;
